<template>
  <BaseModal ref="modal">
    <UiModalDialog class="search-field-expand" :no-closer="true">
      <template #header>{{ ls(def.name) }}</template>
      <template #header-extra>
        <UiInputRound class="jps-round-input" v-model="filter" />
      </template>
      <div class="jps-modal-content">
        <div class="sfde-loader" v-if="loading">
          <div :class="{'hina-logo-icon hina-loading': isHina, 'jps-logo-icon jps-loading': isJps}" />
        </div>
        <UiDbSpan
          :class="{ 'is-selected': isSelected(value) }"
          class="db-row"
          v-for="(count, value) in sortedFacet"
          :key="value"
          tag="div"
          :id="value"
          @click="select(value)"
        >
          <template v-slot="{ db }">
            <div>
              <BaseCheckbox
                :model-value="isSelected(value)"
                @update:model-value="select(value)"
                class="checkbox"
              />
            </div>
            <div class="image-selection">
              <UiImageSelection
                type="database"
                :image="db.image"
                background="transparent"
                image-usage="thumbnail"
                :cover-override="false"
              />
            </div>
            <div class="db-title">
              <div class="db-title-text">
                {{ ls(db.name) }}
              </div>
              <div class="count">{{ facetNum(count) }}</div>
            </div>
            <div class="description">{{ ls(db.description) }}</div>
          </template>
        </UiDbSpan>
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="modal.resolve(selected)">{{ lss('絞り込み', 'Narrow down') }}</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'
import { useSearchFieldExpand } from '~/components/search/search-field/useSearchFieldExpand'
import type { SearchFieldExpandProps } from '~/components/search/search-field/useSearchFieldExpand'

const { ls, lss, num } = useI18n()
const { isJps, isHina } = useServiceType()
const dataStore = useDataStore()

interface Props extends SearchFieldExpandProps {
}
const props = withDefaults(defineProps<Props>(), {
})

const { init, filter, isSelected, selected, select } = useSearchFieldExpand(props)

const modal = ref<InstanceType<typeof BaseModal>>()
const dbFacet: Ref<ItemFacet> = ref(null)
const loading = ref(true)
const openAwait = async () => {
  loading.value = true
  init()
  dbFacet.value = (await props.ss.executeFacet([props.def.key]))[0]
  loading.value = false
  return await modal.value.openAwait<string[]>()
}
defineExpose({ openAwait })

const facetNum = (n: number) => {
  if (n === 0) return '-'
  else return num(n)
}

const sortedFacet = computed(() => {
  if (!dbFacet.value) return {}
  let obj = {}
  let keys = Object.keys(dbFacet.value.counts).filter(key => {
    let values = [key]
    const db = dataStore.database(key)
    if (db) {
      values.push(db.name.ja, db.name.en)
    }
    if (values.length == 0) return false
    return includes(new RegExp(filter.value, 'i'), ...values)
  })
  keys = keys.sort((a, b) => dbFacet.value.counts[b] - dbFacet.value.counts[a])
  keys.forEach(key => {
    obj[key] = props.facet.counts[key] || 0
  })
  return obj
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-round-input {
  background: $white !important;
  width: auto !important;
  margin-left: 1rem  !important;
  margin-right: 1rem  !important;
}
.sfde-loader {
  display: flex;
  justify-content: center;
}
.db-row {
  display: grid;
  grid-template-columns: 2rem 6rem 13rem 1fr;
  gap: 1rem;
  border-bottom: 1px solid $border-grey;
  padding-top: 12px;
  padding-bottom: 12px;
  cursor: pointer;

  .db-title {
    display: flex;
    flex-shrink: 0;
    flex-grow: 1;
    gap: 1rem;

    @include is-mobile {
      flex-direction: column;
      gap: 0;
    }

    .db-title-text {
      @include is-desktop {
        flex: 0 0 8rem;
      }
    }
  }

  .image-selection {
    max-height: 6rem;

    @include is-mobile {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    @include is-tablet {
      width: 6rem;
      height: 6rem;
    }
  }
  &:first-child {
    border-top: 1px solid $border-grey;
  }

  .description {
    font-size: 0.75rem;
  }
  .count {
    text-align: right;
    font-size: 0.75rem;

    @include is-desktop {
      flex: 0 0 4rem;
    }
  }
  @include is-mobile() {
    gap: 0.75rem 0.75rem;
    grid-template-columns: 1.5rem 3rem 1fr;
    grid-template-rows: auto 1fr;
    align-items: center;

    .j-img {
      visibility: hidden;
    }
    .count {
      text-align: left;
    }
    .description {
      grid-column: 1 / 4;
      grid-row: 2 / 3;
    }
  }
}
</style>
